//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  GetSamplingOpportunitiesRequest,
  PurchaseOption,
} from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { useGetSamplingOpportunitiesQuery } from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsQueries';

interface PrepaidUsePurchaseOptionsParams {
  request?: GetSamplingOpportunitiesRequest;
  selectedOptions?: PurchaseOption[];
}

interface PrepaidUsePurchaseOptionsResult {
  error: Error | undefined;
  isFetching: boolean;
  isPending: boolean;
  limit?: number;
  offset?: number;
  purchaseOptions: PurchaseOption[];
  total?: number;
}

/**
 * Hook that fetches purchase options with server-side pagination, sorting, and filtering.
 */
export const usePrepaidPurchaseOptions = ({
  request = {},
  selectedOptions = [],
}: PrepaidUsePurchaseOptionsParams): PrepaidUsePurchaseOptionsResult => {
  const { data, error, isFetching, isPending } =
    useGetSamplingOpportunitiesQuery(request);

  const purchaseOptions = data?.opportunities ?? [];

  const filteredPurchaseOptions = purchaseOptions.filter((option) => {
    // Check if the option is currently selected
    const isSelected = selectedOptions.some(
      (selected) => selected.opportunityId === option.opportunityId,
    );

    // Filter out options with 0 or negative balance, unless it's selected
    // We keep selected options even with 0 balance so we can show proper expiration/invalid messages
    const hasPositiveBalance = (option.balance ?? 0) > 0;

    return hasPositiveBalance || isSelected;
  });

  return {
    error: error ?? undefined,
    isFetching,
    isPending,
    limit: data?.limit,
    offset: data?.offset,
    purchaseOptions: filteredPurchaseOptions,
    total: data?.total,
  };
};
